import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Crown, Calendar, TrendingUp, ShieldCheck, AlertTriangle, ArrowRight, Sparkles, Building2 } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { formatDateAU } from "@/lib/formatDateAU";

/**
 * Membership panel for /customer dashboard.
 * Renders different states:
 *  - inactive: upsell card with "Join" CTA
 *  - active: status, renewal date, savings counter, cancel button
 *  - cancelled: same as active but with "Reactivate" CTA + ends-on warning
 *  - failed: payment-failed banner
 */
export function MembershipCard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  // Phase 2L-A112 — Property Managers (registered partners with
  // partner_type='property_manager') must NOT see the homeowner
  // "Join for $99/year" upsell. They're an approved
  // agency user, not a residential customer.
  const [isPropertyManager, setIsPropertyManager] = useState(false);
  const [pmChecked, setPmChecked] = useState(false);

  const load = () => {
    setLoading(true);
    api.get("/memberships/me")
      .then(({ data }) => setData(data))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
  }, []);

  // Jun 2026 — the auth payload carries is_property_manager (resolved
  // server-side by the same canonical PM resolver), so no 403-probing
  // of /property-manager/me for regular customers.
  const { user } = useAuth();
  useEffect(() => {
    setIsPropertyManager(!!user?.is_property_manager);
    setPmChecked(user !== undefined);
  }, [user]);

  const handleCancel = async () => {
    if (!window.confirm("Cancel your AWARE Water Health Record? You'll keep full access until your renewal date.")) return;
    setBusy(true);
    try {
      await api.post("/memberships/cancel");
      toast.success("Membership cancelled — you'll keep access until your renewal date.");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Cancel failed");
    } finally {
      setBusy(false);
    }
  };

  const handleReactivate = async () => {
    setBusy(true);
    try {
      await api.post("/memberships/reactivate");
      toast.success("Membership reactivated.");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Reactivate failed");
    } finally {
      setBusy(false);
    }
  };

  if (loading || !pmChecked) {
    return (
      <div data-testid="membership-card-loading" className="rounded-3xl bg-white border border-slate-200 p-7 animate-pulse">
        <div className="h-4 w-32 bg-slate-100 rounded mb-3" />
        <div className="h-6 w-48 bg-slate-100 rounded" />
      </div>
    );
  }

  // Phase 2L-A112 — Property Managers see an agency-specific tile
  // instead of the homeowner "Join the Passport" upsell.
  if (isPropertyManager) {
    return (
      <Link
        to="/property-manager"
        data-testid="membership-card-pm-portal"
        className="block rounded-3xl bg-white border border-cyan-200 hover:border-cyan-400 hover:shadow-md transition p-7 lg:p-8"
      >
        <div className="flex items-center gap-3 text-[11px] font-bold text-cyan-700 uppercase tracking-wider">
          <Building2 className="w-4 h-4" /> Property Manager
        </div>
        <h2 className="mt-2 font-display text-2xl font-bold text-slate-900">
          Manage AWARE Water Health Records for your managed properties
        </h2>
        <p className="mt-2 text-slate-600 text-sm leading-relaxed">
          View the properties assigned to your agency, track Water
          Health Scores, and monitor annual retest dates from one
          dashboard.
        </p>
        <div className="mt-4 inline-flex items-center gap-1.5 text-cyan-700 text-sm font-semibold">
          Open Property Manager portal <ArrowRight className="w-4 h-4" />
        </div>
      </Link>
    );
  }

  const status = data?.status || "inactive";
  const periodEnd = data?.current_period_end || null;
  const periodEndPretty = formatDateAU(periodEnd);

  // Jun 2026 — the $99/yr membership is RETIRED. The Water Health Record is
  // free with every test, so non-members see nothing here (the free record
  // banner below covers it). Existing members still see their status card.
  if (status === "inactive" || status === "expired") return null;

  // ACTIVE / CANCELLED / FAILED
  const statusBadge = {
    active: { label: "Active",     bg: "bg-emerald-100", text: "text-emerald-800", dot: "bg-emerald-500" },
    cancelled: { label: "Cancels at period end", bg: "bg-amber-100", text: "text-amber-800", dot: "bg-amber-500" },
    failed: { label: "Payment failed", bg: "bg-rose-100", text: "text-rose-800", dot: "bg-rose-500" },
  }[status] || { label: status, bg: "bg-slate-100", text: "text-slate-700", dot: "bg-slate-400" };

  return (
    <div
      data-testid="membership-card-active"
      className="rounded-3xl bg-gradient-to-br from-slate-900 via-slate-900 to-cyan-900 text-white p-7 lg:p-8 relative overflow-hidden"
    >
      <div className="absolute -top-24 -right-16 w-72 h-72 bg-cyan-500/20 rounded-full blur-3xl pointer-events-none" />
      <div className="relative">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <div className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-[0.22em] font-bold text-cyan-300">
              <Crown className="w-3.5 h-3.5" /> AWARE Water Health Record
            </div>
            <h3 className="font-display mt-2 text-2xl lg:text-3xl font-bold">You're a member ✓</h3>
          </div>
          <span data-testid="membership-status-badge" className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold ${statusBadge.bg} ${statusBadge.text}`}>
            <span className={`w-1.5 h-1.5 rounded-full ${statusBadge.dot}`} />
            {statusBadge.label}
          </span>
        </div>

        {status === "failed" && (
          <div className="mt-5 rounded-2xl bg-rose-500/15 border border-rose-400/40 p-4 flex items-start gap-2.5 text-sm">
            <AlertTriangle className="w-4 h-4 text-rose-300 mt-0.5 flex-shrink-0" />
            <div>Your last renewal payment failed. Update your card in Stripe and we'll retry automatically.</div>
          </div>
        )}

        <div className="mt-6 grid sm:grid-cols-3 gap-4">
          <div data-testid="membership-savings" className="rounded-2xl bg-white/10 border border-white/10 backdrop-blur p-4">
            <div className="flex items-center gap-2 text-[10px] uppercase tracking-wider font-bold text-cyan-300">
              <TrendingUp className="w-3.5 h-3.5" /> Tests this period
            </div>
            <div className="font-display text-3xl font-bold mt-1.5">{data.tests_completed || 0}</div>
            <div className="text-[11px] text-slate-300 mt-0.5">
              {data.tests_completed > 0 ? "logged in your record" : "Book a test to start building your record"}
            </div>
          </div>
          <div className="rounded-2xl bg-white/10 border border-white/10 backdrop-blur p-4">
            <div className="flex items-center gap-2 text-[10px] uppercase tracking-wider font-bold text-cyan-300">
              <Calendar className="w-3.5 h-3.5" /> {status === "cancelled" ? "Access ends" : "Renews on"}
            </div>
            <div className="font-display text-lg font-bold mt-1.5">{periodEndPretty}</div>
            <div className="text-[11px] text-slate-300 mt-0.5">
              {data.days_until_renewal != null ? `${data.days_until_renewal} days away` : "—"}
            </div>
          </div>
          <div className="rounded-2xl bg-white/10 border border-white/10 backdrop-blur p-4">
            <div className="flex items-center gap-2 text-[10px] uppercase tracking-wider font-bold text-cyan-300">
              <ShieldCheck className="w-3.5 h-3.5" /> Property coverage
            </div>
            <div className="font-display text-3xl font-bold mt-1.5">{data.property_coverage ?? 0}</div>
            <div className="text-[11px] text-slate-300 mt-0.5">
              {data.property_coverage === 1 ? "property monitored" : "properties monitored"}
            </div>
          </div>
        </div>

        <div className="mt-6 flex flex-wrap items-center gap-3">
          <Link to="/passport" data-testid="membership-view-passport" className="inline-flex items-center gap-1.5 px-5 py-2.5 rounded-full bg-white text-slate-900 hover:bg-cyan-100 text-sm font-semibold transition-colors">
            <Sparkles className="w-4 h-4" /> View my record
          </Link>
          <Link to="/book-water-test" data-testid="membership-book-test" className="inline-flex items-center gap-1.5 px-5 py-2.5 rounded-full bg-white/10 border border-white/20 hover:bg-white/20 text-sm font-semibold transition-colors">
            Book your next test <ArrowRight className="w-4 h-4" />
          </Link>
          {status === "active" && (
            <button
              data-testid="membership-cancel-button"
              onClick={handleCancel}
              disabled={busy}
              className="ml-auto text-xs text-slate-300 hover:text-white font-medium underline-offset-4 hover:underline"
            >
              Cancel membership
            </button>
          )}
          {status === "cancelled" && (
            <button
              data-testid="membership-reactivate-button"
              onClick={handleReactivate}
              disabled={busy}
              className="ml-auto inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-emerald-500 hover:bg-emerald-400 text-white text-xs font-semibold"
            >
              Reactivate
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
